/* global Vue, VueI18n, primevue, LOGO, HOME_TITLE, HOME_URL, AZURE_URI, LOCALE, DESTINATIONS, BOOKING_WIDGET_CMS_DATA, AlgoliaEvents */

import { CreateI18n } from '../i18n/create-i18n'
import { messagesSpecific } from '../i18n/translations'
import { useBookingParams } from '../composables/useBookingParams'
import BookingCalendar from './components/BookingCalendar'
import BookingGuests from './components/BookingGuests'
import { AvailabilityCalendar } from '../classes/AvailabilityCalendar'
import { displayDate, displayGuests, localizeCalendar, getHashParam, setHashParam, removeHashParam } from '../utils-common'

const i18n = CreateI18n(messagesSpecific)

const BookingWidgetApp = Vue.createApp({
  name: 'BookingWidgetApp',
  components: { BookingCalendar, BookingGuests },
  template: `
  <div>
    <Teleport to="#booking-widget-trigger" v-if="triggerExistsOnPage">
      <div class="booking-widget-trigger">
        <div class="booking-widget-trigger__fields">
          <div class="booking-widget-trigger__field" @click="isActive = true; level = 'property'">
            <div>
              <i class="icon-pin"></i>
              <span>{{ propertySearch || $t('bookingWidget.whereAreYouGoing') }}</span>
            </div>
          </div>
          <div class="booking-widget-trigger__field booking-widget-trigger__date" @click="isActive = true; level = 'calendar'">
            <div>
              <i class="icon-calendar"></i>
              <span>{{ dateInputValue }}</span>
            </div>
          </div>
          <div class="booking-widget-trigger__field booking-widget-trigger__guests" @click="isActive = true; level = 'guests'">
            <div>
              <i class="icon-adult"></i>
              <span>{{ guestsInputValue }}</span>
            </div>
          </div>
        </div>
        <button class="booking-widget-trigger__button button" @click="bookNowAsync">
          {{ $t('bookNow') }}
        </button>
      </div>
    </Teleport>

    <Teleport to="#booking-bar" v-if="bookingBarExistsOnPage">
      <div class="booking-bar">
        <button class="button button--primary hide-md-up" @click="isActive = true">
          {{ $t('bookNow') }}
        </button>

        <div class="booking-bar__wrapper hide-md-down">
          <button class="booking-bar__button" @click="isActive = true; level = 'calendar'">
            <i class="icon-calendar"></i>
            {{ dateInputValue }}
          </button>

          <button class="booking-bar__button" @click="isActive = true; level = 'guests'">
            <i class="icon-adult"></i>
            {{ guestsInputValue }}
          </button>

          <button @click="bookNowAsync" class="button button--primary" v-text="$t('bookNow')" />
        </div>
      </div>
    </Teleport>

    <div class="booking-widget" v-if="isActive" :class="{'booking-widget--has-popover': level !== null, 'booking-widget--closing': closing}">
      <header class="header header--simple">
        <div class="header__top">
          <div class="header-logos">
            <a :href="HOME_URL" :title="HOME_TITLE" class="header-logos__maistra">
              <picture>
                <source media="(min-width: 992px)" :srcset="LOGO.desktop">
                <img :src="LOGO.mobile" :alt="HOME_TITLE" width="48" height="48">
              </picture>
            </a>
          </div>
          <div class="header__icons">
            <button class="button button--blank" @click="closeWidget" :title="$t('close')">
              <span class="hide-sm-down">{{ $t('close') }}</span>
              <i class="icon-close"></i>
            </button>
          </div>
        </div>
      </header>
      <div class="booking-widget__content">
        <div class="h1 no-margin-top">
          {{ $t('bookingWidget.planYourStay') }}
          <button class="button button--blank mobile-only" @click="closeWidget" :title="$t('close')">
            <span class="hide-sm-down">{{ $t('close') }}</span>
            <i class="icon-close"></i>
          </button>
        </div>

        <div class="booking-widget__container">
          <div class="booking-widget__form">
            <div class="booking-widget__fields">
              <div>
                <label for="bookingWidgetProperty">{{ $t('bookingModal.property') }}</label>
                <i class="icon-pin"></i>
                <input type="text" id="bookingWidgetProperty" :placeholder="$t('bookingWidget.whereAreYouGoing')" autocomplete="off" v-model="propertySearch" @focus="handlePropertyInputFocus" />
              </div>
              <div class="date">
                <label for="bookingWidgetDate">{{ $t('bookingModal.date') }}</label>
                <i class="icon-calendar"></i>
                <input type="text" id="bookingWidgetDate" placeholder="Check-in - Check-out" @focus="isActive = true; level = 'calendar'" :value="dateInputValue" readonly />
              </div>
              <div class="guests">
                <label for="bookingWidgetGuests">{{ $t('bookingModal.guests') }}</label>
                <i class="icon-adult"></i>
                <input type="text" id="bookingWidgetGuests" @focus="handleGuestsInputFocus" :value="guestsInputValue" readonly />
                <div
                  v-if="guestsAdjustedWarning"
                  class="warning-message">
                  <i class="icon-warning"></i>
                  {{ $t('bookingModal.guestsAdjusted') }}
                </div>
              </div>
              <div v-if="isActive" class="booking-widget__access-code" :class="{'error': accessCodeInfo.isValid === false && accessCode !== ''}">
                <label for="bookingWidgetCode">{{ $t('bookingModal.accessCode') }}</label>
                <i class="icon-fee"></i>
                <input type="text" id="bookingWidgetCode" :placeholder="$t('accessCode.placeholder')" v-model="accessCode" />
                <button @click="accessCode = ''; accessCodeInfo.isValid = null"><i class="icon-close"></i></button>
                <div
                  v-if="accessCodeInfo.isValid === false && accessCode !== ''"
                  class="error-message">
                  {{ accessCodeInfo.isVoucher ? $t('bookingModal.accessCodeIsVoucher') : $t('accessCode.invalidAccessCode') }}
                </div>
              </div>
            </div>
            <button class="booking-widget__button button" @click="bookNowAsync">
              {{ $t('bookNow') }}
            </button>
          </div>
          <div class="booking-widget__destination popover" v-if="level === 'property'">
            <div class="h3">
              <button @click="closePropertyPopover"><i class="icon-caret-left"></i></button><div>{{ $t('bookingModal.property') }}</div>
            </div>
            <div class="popover-content">
              <label>
                <i class="icon-pin"></i>
                <input type="text" id="bookingWidgetPropertyPopover" :placeholder="$t('bookingWidget.whereAreYouGoing')" autocomplete="off" v-model="propertySearch" @focus="propertySearch = ''" />
              </label>
              <div v-if="propertySearch.length === 0 || (destinationResults.length + propertyResults.length === 0)" class="search-results">
                <div v-if="propertySearch.length > 0 && !property" class="no-results">
                  <div class="h4 no-margin">{{ $t('bookingWidget.noResultsTitle') }}</div>
                  <div>{{ $t('bookingWidget.noResultsMessage') }}</div>
                </div>
                <div>
                  <div class="title">{{ BOOKING_WIDGET_CMS_DATA.topPropertiesTitle }}</div>
                  <ul class="no-bullets">
                    <li v-for="p in propertySuggestions" :key="p.phobsId">
                      <button @click="updateProperty(p)">
                        <span>{{ p.title }}</span>
                        <span class="stars stars--small" :class="'theme--' + p.brand">
                          <i v-for="n in p.stars" class="icon-star"></i>
                        </span>
                      </button>
                    </li>
                  </ul>
                </div>
                <div>
                  <div class="title">{{ BOOKING_WIDGET_CMS_DATA.topDestinationsTitle }}</div>
                  <ul class="no-bullets">
                    <li v-for="destination in destinationSuggestions" :key="destination.slug">
                      <button @click="updateDestination(destination)">
                        <i class="icon-pin"></i>
                        <span>{{ destination.title }}</span>
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
              <div v-else class="search-results">
                <div v-if="destinationResults.length > 0">
                  <div class="title">{{ $t('destination') }}</div>
                  <ul class="no-bullets">
                    <li v-for="destination in destinationResults" :key="destination.slug">
                      <button @click="updateDestination(destination)">
                        <i class="icon-pin"></i>
                        <span>{{ destination.title }}</span>
                      </button>
                    </li>
                  </ul>
                </div>
                <div v-if="propertyResults.length > 0">
                  <div class="title">{{ $t('focusedSearch.properties') }}</div>
                  <ul class="no-bullets">
                    <li v-for="p in propertyResults" :key="p.phobsId">
                      <button @click="updateProperty(p)">
                        <span>{{ p.title }}</span>
                        <span class="stars stars--small" :class="'theme--' + p.brand">
                          <i v-for="n in p.stars" class="icon-star"></i>
                        </span>
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          <div class="booking-widget__calendar popover" v-if="level === 'calendar'">
            <div class="h3">
              <button @click="level = null"><i class="icon-caret-left"></i></button><div>{{ $t('bookingModal.date') }}</div>
            </div>
            <div class="popover-content">
              <BookingCalendar
                noButton
                :startDate="startDate"
                :nights="nights"
                :availabilityCalendar="availabilityCalendar"
                @dateSelected="updateCalendar" />
            </div>
            <div>
              <button class="button button--primary" @click="applyCalendar">{{ $t('select') }}</button>
            </div>
          </div>
          <div class="booking-widget__guests popover" v-if="level === 'guests'">
            <div class="h3">
              <button @click="level = null"><i class="icon-caret-left"></i></button><div>{{ $t('bookingModal.guests') }}</div>
            </div>
            <div class="popover-content">
              <BookingGuests :adults="adults" :children="children" :property="property" @updateGuests="updateGuests" noButton />
            </div>
            <div>
              <button class="button button--primary" @click="applyGuests">{{ $t('select') }}</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  `,
  setup () {
    const { t } = VueI18n.useI18n()
    const isActive = Vue.ref(false)
    const triggerExistsOnPage = Vue.ref(false)
    const bookingBarExistsOnPage = Vue.ref(false)
    const closing = Vue.ref(false)

    const level = Vue.ref(null)

    const allProperties = Vue.ref([])
    const { property, destination, startDate, nights, adults, children, guestsAdjustedWarning, accessCode, bookingUrl, checkAccessCodeAsync, loadDataFromHashParams } = useBookingParams()
    const availabilityCalendar = Vue.reactive(new AvailabilityCalendar())

    if (getHashParam('bookingWidgetOpen') === 'true') {
      handleExternalTrigger({ detail: { level: 'property', propertyId: getHashParam('property') } })
      loadDataFromHashParams()
    }

    const propertySearch = Vue.ref('')
    const propertyResults = Vue.computed(() => {
      if (propertySearch.value.length === 0) {
        return []
      }

      const search = propertySearch.value.toLowerCase().split(' ')

      return allProperties.value
        .filter(p => search.every(s => p.searchTokens.some(t => t.startsWith(s))))
        .sort((a, b) => {
          const aPos = Math.min(...search.map(s => a.searchTokens.findIndex(t => t.startsWith(s))).filter(i => i !== -1))
          const bPos = Math.min(...search.map(s => b.searchTokens.findIndex(t => t.startsWith(s))).filter(i => i !== -1))
          return aPos - bPos
        })
    })
    const propertySuggestions = Vue.computed(() => {
      return BOOKING_WIDGET_CMS_DATA.topProperties
        .map(p => allProperties.value
          .find(prop => `${prop.propertyGroup}/_index` === p.property)).filter(p => p)
    })
    const destinationResults = Vue.computed(() => {
      if (propertySearch.value.length === 0) {
        return []
      }

      return DESTINATIONS.filter(d => d.title.toLowerCase().startsWith(propertySearch.value.toLowerCase()))
    })
    const destinationSuggestions = Vue.computed(() => {
      return BOOKING_WIDGET_CMS_DATA.topDestinations.map(d => DESTINATIONS.find(dest => dest.slug === d.destination)).filter(d => d)
    })

    const unappliedCalendar = Vue.ref(null)
    const dateInputValue = Vue.computed(() => (
      startDate.value && nights.value)
      ? displayDate({ startDate: startDate.value, nights: nights.value })
      : '')

    const unappliedGuests = Vue.ref(null)
    const guestsInputValue = Vue.computed(() => (
      displayGuests({ adults: adults.value, children: children.value }, t, property?.maxChildAge === 0)
    ))

    const accessCodeInfo = Vue.reactive({
      code: accessCode.value,
      isValid: null,
      isRemoved: false,
    })

    let savedScrollY = 0

    Vue.watch(isActive, (show) => {
      if (show) {
        savedScrollY = window.scrollY
        document.body.classList.add('booking-widget-active')
        window.scrollTo({ top: 0 })
        getPropertiesAsync()
        setTimeout(() => {
          document.addEventListener('mouseup', clickOutsideEvent)

          if (level.value === 'property' && propertySearch.value.length === 0) {
            focusPropertyInput()
          }
        }, 300)
        setHashParam('bookingWidgetOpen', 'true')
      } else {
        closing.value = false
        document.body.classList.remove('booking-widget-active')
        window.scrollTo({ top: savedScrollY })
        document.removeEventListener('mouseup', clickOutsideEvent)
        if (!property.value && !destination.value) {
          propertySearch.value = ''
        }
        removeHashParam('bookingWidgetOpen')
        removeHashParam('property')
        removeHashParam('startDate')
        removeHashParam('nights')
        removeHashParam('guests')
        removeHashParam('code')
      }
    }, { immediate: true })

    function focusPropertyInput () {
      let inputEl
      if (window.matchMedia(`(min-width: ${window.BREAKPOINTS.sm}px)`).matches) {
        inputEl = document.getElementById('bookingWidgetProperty')
      } else {
        inputEl = document.getElementById('bookingWidgetPropertyPopover')
      }
      inputEl.focus()
      inputEl.click() // for the iOS
    }

    function closeWidget () {
      closing.value = true
      setTimeout(() => {
        isActive.value = false
      }, 30)
    }

    Vue.watch(level, () => {
      Vue.nextTick(() => {
        window.scrollTo({ top: 0 })
        window.scrollTo(window.scrollX, window.scrollY + 1)
        window.scrollTo(window.scrollX, window.scrollY - 1)
      })
    })

    async function getPropertiesAsync () {
      if (allProperties.value.length === 0) {
        const response = await fetch(`${AZURE_URI}GetProperties?locale=${LOCALE}`, { priority: 'high' })
        allProperties.value = (await response.json()).filter(p => p.phobsId).map(p => ({
          ...p,
          searchTokens: [
            ...p.title.toLowerCase().split(' ').filter(w => w.length > 2),
            p.destination,
            p.menuGroup,
            t(`propertyTypes.${p.menuGroup}`).toLowerCase(),
            ...(p.bundleTitle ? p.bundleTitle.toLowerCase().split(' ').filter(w => w.length > 2) : []),
          ],
        }))
      }
    }

    function updateDestination (e) {
      propertySearch.value = e.title
      destination.value = e
      property.value = null
      level.value = window.matchMedia('(max-width: 768px)').matches ? null : 'calendar'
      availabilityCalendar.reset()
    }

    function updateProperty (e, passive = false) {
      property.value = e
      propertySearch.value = e.title
      destination.value = null
      availabilityCalendar.loadAvailability({ propertyIds: [e.phobsId], accessCode: accessCode.value })

      if (!passive) level.value = window.matchMedia('(max-width: 768px)').matches ? null : 'calendar'
    }

    function updateCalendar (e) {
      if (window.matchMedia('(max-width: 768px)').matches) {
        unappliedCalendar.value = {
          startDate: e.startDate,
          nights: e.nights,
        }
        return
      }
      startDate.value = e.startDate
      nights.value = e.nights
    }

    function applyCalendar () {
      if (unappliedCalendar.value) {
        startDate.value = unappliedCalendar.value.startDate
        nights.value = unappliedCalendar.value.nights
        unappliedCalendar.value = null
      }

      level.value = null
    }

    function updateGuests (e) {
      if (window.matchMedia('(max-width: 768px)').matches) {
        unappliedGuests.value = {
          adults: e.adults,
          children: [...e.children],
        }
        return
      }

      adults.value = e.adults
      children.value = [...e.children]
    }

    function applyGuests () {
      if (unappliedGuests.value) {
        adults.value = unappliedGuests.value.adults
        children.value = unappliedGuests.value.children
      }
      level.value = null
    }

    async function bookNowAsync () {
      if (property.value && !BOOKING_WIDGET_CMS_DATA.search) {
        setHashParam('property', property.value.phobsId)
      }
      setHashParam('startDate', `${startDate.value.toISOString().substring(0, 10)}`)
      setHashParam('nights', `${nights.value}`)
      setHashParam('guests', `${adults.value}${children.value.length ? ';' : ''}${children.value.map(child => child.age).join(';')}`)
      accessCode.value ? setHashParam('code', accessCode.value) : removeHashParam('code')

      if (!isActive.value && propertySearch.value.length === 0) {
        isActive.value = true
        level.value = 'property'
        return
      }

      guestsAdjustedWarning.value = false
      const accessCodeResult = await checkAccessCodeAsync()

      accessCodeInfo.code = accessCode.value
      accessCodeInfo.isValid = accessCodeResult.isValid

      if (!accessCodeResult.isValid) {
        isActive.value = true
        return
      }

      if (property.value && AlgoliaEvents) {
        AlgoliaEvents.convertedFilters({
          eventName: 'Booking Intent',
          ...AlgoliaEvents.propertyParamsCache,
        })
      }

      window.location = bookingUrl.value
    }

    function handlePropertyInputFocus () {
      isActive.value = true
      level.value = 'property'
      propertySearch.value = ''
    }

    function handleGuestsInputFocus () {
      isActive.value = true
      level.value = 'guests'
      guestsAdjustedWarning.value = false
    }

    function closePropertyPopover () {
      if (propertySearch.value.length === 0 || (!property.value && !destination.value)) {
        propertySearch.value = t('offers.allProperties')
        property.value = null
        destination.value = null
        availabilityCalendar.reset()
      }

      if (property.value) {
        propertySearch.value = property.value.title
      }

      if (destination.value) {
        propertySearch.value = destination.value.title
      }
      level.value = null
    }

    function clickOutsideEvent (e) {
      const path = e.composedPath()

      if (level.value === 'property' && !path.some(el => el.matches?.('.booking-widget__destination')) && !path.some(el => el.matches?.('#bookingWidgetProperty'))) {
        closePropertyPopover()
      }

      if (level.value === 'calendar' && !path.some(el => el.matches?.('.booking-widget__calendar')) && !path.some(el => el.matches?.('#bookingWidgetDate'))) {
        level.value = null
      }

      if (level.value === 'guests' && !path.some(el => el.matches?.('.booking-widget__guests')) && !path.some(el => el.matches?.('#bookingWidgetGuests'))) {
        level.value = null
      }
    }

    async function handleExternalTrigger ({ detail: data }) {
      level.value = data.level || 'property'
      isActive.value = true

      if (data.accessCode) {
        accessCode.value = data.accessCode
      }

      if (data.adults) {
        adults.value = data.adults
      }

      if (data.children) {
        children.value = data.children
      }

      const propertyId = data.propertyId || window.PROPERTY_PHOBS_ID
      if (propertyId) {
        await getPropertiesAsync()

        const property = allProperties.value.find(p => p.phobsId === propertyId)
        if (property) {
          updateProperty(property, true)
        }

        if (!data.level) {
          level.value = 'calendar'
        }
      } else if (BOOKING_WIDGET_CMS_DATA.search) {
        await getPropertiesAsync()
        propertySearch.value = BOOKING_WIDGET_CMS_DATA.search
      }
    }

    Vue.onMounted(async () => {
      window.addEventListener('open-booking-overlay', handleExternalTrigger)
      triggerExistsOnPage.value = !!document.getElementById('booking-widget-trigger')
      bookingBarExistsOnPage.value = !!document.getElementById('booking-bar')

      const propertyId = window.PROPERTY_PHOBS_ID
      if (propertyId) {
        await getPropertiesAsync()

        const property = allProperties.value.find(p => p.phobsId === propertyId)
        if (property) {
          updateProperty(property, true)
        }
      } else if (BOOKING_WIDGET_CMS_DATA.search) {
        await getPropertiesAsync()
        propertySearch.value = BOOKING_WIDGET_CMS_DATA.search
      }
    })

    Vue.onUnmounted(() => {
      window.removeEventListener('open-booking-overlay', handleExternalTrigger)
    })

    return {
      LOGO,
      HOME_TITLE,
      HOME_URL,
      BOOKING_WIDGET_CMS_DATA,
      isActive,
      triggerExistsOnPage,
      bookingBarExistsOnPage,
      closing,
      level,
      property,
      propertySearch,
      propertyResults,
      propertySuggestions,
      destinationResults,
      destinationSuggestions,
      availabilityCalendar,
      dateInputValue,
      guestsInputValue,
      startDate,
      nights,
      adults,
      children,
      guestsAdjustedWarning,
      accessCode,
      accessCodeInfo,
      handlePropertyInputFocus,
      closePropertyPopover,
      updateDestination,
      updateProperty,
      updateCalendar,
      applyCalendar,
      handleGuestsInputFocus,
      updateGuests,
      applyGuests,
      bookNowAsync,
      closeWidget,
    }
  },
})

const appElement = document.getElementById('booking-widget-app')

if (appElement) {
  BookingWidgetApp.use(i18n)
  BookingWidgetApp.use(primevue, {
    locale: localizeCalendar(LOCALE),
  })
  BookingWidgetApp.mount(appElement)
}

window.triggerBookingWidget = function (data) {
  const event = new CustomEvent('open-booking-overlay', {
    detail: data,
  })
  window.dispatchEvent(event)
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.button--primary[href="#/check-rates"]').forEach(button => {
    button.addEventListener('click', (e) => {
      e.preventDefault()
      window.triggerBookingWidget({ source: 'check-rates-button' })
    })
  })
})

window.addEventListener('pageshow', function (event) {
  if (event.persisted && getHashParam('bookingWidgetOpen') === 'true') {
    // this here is for when the user clicked the back browser button
    // in some scenarios it will serve a cached state of the page and booking widget
    // with the call bellow we "reset" the state to what would normally happen - show the
    // appropriate suggestions instead of keeping the last selection for the property
    window.triggerBookingWidget({})
  }
})

export default BookingWidgetApp
